import { Query, useQuery, UseQueryResult } from "@tanstack/react-query"

import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { queryKeys } from "api/queryKeys"
import {
  getQuestionnaireAudit,
  verifyQuestionnaireAudit,
} from "api/Repos/questionnaire"
import { checkDatamapDrift } from "api/Repos/drift"
import { getProjectVersion } from "api/Engine/projectVersion"
import { getProjectAncestry } from "api/Engine/lineage"
import { ProjectAncestry } from "types/ancestry.types"
import {
  AuditChain,
  AuditVerifyResult,
  DatamapDriftResult,
} from "types/questionnaire.types"
import { ProjectVersion } from "types/projectVersion.types"
import { useProjectOwner } from "./useProjectOwner"
import { isRefusedRequest } from "../utils/refusedRequest"

/**
 * Cached reads of the open project's history (react-query). The point of the cache is
 * invalidation: a checkpoint save, a data import or a recorded export invalidates
 * `queryKeys.provenance.project(id)`, so the Timeline, the drift banner and the version ID
 * refresh themselves. The owner comes from the open project; callers pass only its id.
 *
 * The reads are silent: each component that shows one says inline when it couldn't be read,
 * so a toast would say it twice.
 */

export const useProjectAuditQuery = (
  projectId: string,
  { enabled = true }: { enabled?: boolean } = {}
): UseQueryResult<AuditChain> => {
  const getAxios = useAuthedAxios()
  const owner = useProjectOwner()
  return useQuery({
    queryKey: queryKeys.provenance.audit(projectId, owner),
    enabled: enabled && !!projectId && !!owner,
    meta: { silent: true },
    queryFn: async (): Promise<AuditChain> =>
      (await getQuestionnaireAudit(await getAxios(), projectId, owner)).data,
  })
}

const VERIFY_RECHECK_MS = 30_000

// Every 30 s while the check couldn't run or the request failed on the way (network, 5xx).
const verifyRecheckMs = (query: Query<AuditVerifyResult>): number | false => {
  const { status, error, data } = query.state
  if (status === "error") return isRefusedRequest(error) ? false : VERIFY_RECHECK_MS
  return data?.checked === false ? VERIFY_RECHECK_MS : false
}

/**
 * The server's check of the project history. A check that couldn't run (Repos unreachable)
 * answers `checked: false`; that, or a request that failed on the way (network, 5xx), is asked
 * again every 30 s, so the Timeline's "couldn't check" banner clears by itself once the server is
 * back. A refused request (4xx) is not asked again. Silent: a failing recheck must not toast
 * every 30 s; the banner shows the state.
 */
export const useProjectAuditVerifyQuery = (
  projectId: string,
  { enabled = true }: { enabled?: boolean } = {}
): UseQueryResult<AuditVerifyResult> => {
  const getAxios = useAuthedAxios()
  const owner = useProjectOwner()
  return useQuery({
    queryKey: queryKeys.provenance.verify(projectId, owner),
    enabled: enabled && !!projectId && !!owner,
    meta: { silent: true },
    refetchInterval: verifyRecheckMs,
    queryFn: async (): Promise<AuditVerifyResult> =>
      (await verifyQuestionnaireAudit(await getAxios(), projectId, owner)).data,
  })
}

/**
 * How the loaded data differs from the questionnaire. Advisory (a banner, the export modal),
 * so silent: callers show `isError` where it matters.
 */
export const useDriftQuery = (
  projectId: string,
  enabled: boolean = true
): UseQueryResult<DatamapDriftResult> => {
  const getAxios = useAuthedAxios()
  const owner = useProjectOwner()
  return useQuery({
    queryKey: queryKeys.provenance.drift(projectId, owner),
    enabled: enabled && !!projectId && !!owner,
    meta: { silent: true },
    queryFn: async (): Promise<DatamapDriftResult> =>
      (await checkDatamapDrift(await getAxios(), projectId, owner)).data,
  })
}

export const useProjectVersionQuery = (
  projectId: string
): UseQueryResult<ProjectVersion> => {
  const getAxios = useAuthedAxios()
  const owner = useProjectOwner()
  return useQuery({
    queryKey: queryKeys.provenance.projectVersion(projectId, owner),
    enabled: !!projectId && !!owner,
    meta: { silent: true },
    queryFn: async (): Promise<ProjectVersion> =>
      (await getProjectVersion(await getAxios(), projectId, owner)).data,
  })
}

export const useProjectAncestryQuery = (
  projectId: string
): UseQueryResult<ProjectAncestry> => {
  const getAxios = useAuthedAxios()
  const owner = useProjectOwner()
  return useQuery({
    queryKey: queryKeys.provenance.ancestry(projectId, owner),
    enabled: !!projectId && !!owner,
    meta: { silent: true },
    queryFn: async (): Promise<ProjectAncestry> =>
      (await getProjectAncestry(await getAxios(), projectId, owner)).data,
  })
}
